import React, { useEffect, useMemo, useState } from "react";
import { showActionError } from "@/lib/feedback";
import { getApiErrorMessage } from "@/utils/apiErrors";
import type { ApiError } from "@/types/api";
import { GitBranch, X } from "lucide-react";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { useModalDismiss } from "@/hooks/useModalDismiss";
import { useCountries } from "@/hooks/useCountries";
import { profileService, LegalEntityHierarchy } from "@/services/profileService";
import { LogoUploadField } from "@/components/molecules/LogoUploadField";
import { normalizeLogoUrlForApi } from "@/lib/logoUpload";
import { resolvePhoneCodeOptionValue, sanitizeNationalPhoneInput } from "@/modules/contacts/contactFormUtils";
import { EMPTY_ENTITY_ADDRESS, EntityAddressFields } from "./EntityAddressFields";
import {
  extractCreatedEntityId,
  persistBranchAddress,
  pickAddressValues,
  toBranchAddressPayload,
} from "./entityAddressUtils";

interface Props {
  callerEmail: string;
  legalEntities: LegalEntityHierarchy[];
  defaultLegalEntityId?: string | null;
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const CreateBranchDialog: React.FC<Props> = ({
  callerEmail,
  legalEntities,
  defaultLegalEntityId,
  open,
  onClose,
  onCreated,
}) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneDialCode: "+44",
    phoneCountryCode: "GB",
    phoneNumber: "",
    legalEntityId: defaultLegalEntityId ?? legalEntities[0]?.id ?? "",
    logoUrl: "",
    ...EMPTY_ENTITY_ADDRESS,
  });
  const [errors, setErrors] = useState<Partial<Record<"name" | "email" | "legalEntityId", string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const { phoneCodeOptions, loading: loadingCountries } = useCountries();

  const phoneCodeValue = useMemo(
    () => resolvePhoneCodeOptionValue(form.phoneDialCode, form.phoneCountryCode, phoneCodeOptions),
    [form.phoneDialCode, form.phoneCountryCode, phoneCodeOptions],
  );

  const entityOptions = useMemo(
    () =>
      flattenEntities(legalEntities).map((le) => ({
        value: le.id,
        label: le.name,
        depth: le.depth,
      })),
    [legalEntities],
  );

  const branchNameDuplicate = useMemo(() => {
    const name = form?.name?.trim();
    if (!name || !form?.legalEntityId) return false;
    const entity = flattenEntities(legalEntities).find((le) => le.id === form?.legalEntityId);
    const normalized = name.toLowerCase();
    return entity?.branches.some((b) => (b.name ?? "").trim().toLowerCase() === normalized) ?? false;
  }, [form.name, form.legalEntityId, legalEntities]);

  const branchEmailDuplicate = useMemo(() => {
    const email = form?.email?.trim().toLowerCase();
    if (!email) return false;
    return flattenEntities(legalEntities).some((entity) =>
      entity.branches.some((branch) => branch.email?.trim().toLowerCase() === email),
    );
  }, [form.email, legalEntities]);

  useEffect(() => {
    if (open) {
      setForm({
        name: "",
        email: "",
        phoneDialCode: "+44",
        phoneCountryCode: "GB",
        phoneNumber: "",
        legalEntityId: defaultLegalEntityId ?? legalEntities[0]?.id ?? "",
        logoUrl: "",
        ...EMPTY_ENTITY_ADDRESS,
      });
      setErrors({});
    }
  }, [open, defaultLegalEntityId, legalEntities]);

  const setField = <K extends keyof typeof form>(key: K, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (key in errors) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const { onOverlayClick } = useModalDismiss(open, onClose);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const branchEmail = form?.email?.trim() || null;
    const errs: typeof errors = {};
    if (!form?.legalEntityId) errs.legalEntityId = "Please select a legal entity.";
    if (!form?.name?.trim()) errs.name = "Branch name is required.";
    else if (branchNameDuplicate) errs.name = "A branch with this name already exists.";
    if (branchEmail && branchEmailDuplicate) {
      errs.email = "A branch with this email already exists.";
    }
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    const branchName = form?.name?.trim();
    const nationalPhone = form.phoneNumber.trim();
    const addressPayload = toBranchAddressPayload(pickAddressValues(form));
    setSubmitting(true);
    let createdBranchId = "";
    try {
      const created = await profileService.createBranch(callerEmail, {
        name: branchName,
        legalEntityId: form?.legalEntityId,
        logoUrl: normalizeLogoUrlForApi(form.logoUrl) || null,
        phoneNumber: nationalPhone ? `${form.phoneDialCode}${nationalPhone}` : null,
      });
      createdBranchId = extractCreatedEntityId(created);
      if (createdBranchId) {
        try {
          await profileService.updateEntity(
            callerEmail,
            "BRANCH",
            createdBranchId,
            {
              name: branchName,
              ...(branchEmail ? { email: branchEmail } : {}),
              phoneNumber: nationalPhone ? `${form.phoneDialCode}${nationalPhone}` : null,
              logoUrl: normalizeLogoUrlForApi(form.logoUrl) || null,
            },
            form.legalEntityId,
          );
          await persistBranchAddress(createdBranchId, addressPayload);
        } catch (updateErr: unknown) {
          await profileService
            .deleteEntity(callerEmail, "BRANCH", createdBranchId)
            .catch(() => undefined);
          throw updateErr;
        }
      }
      onCreated();
      onClose();
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      let message = getApiErrorMessage(err) ?? "Failed to create branch";
      if (apiErr.code === "DUPLICATE_GROUP_NAME" && branchName) {
        message = `A branch named '${branchName}' already exists.`;
      }
      if (apiErr.code === "DUPLICATE_BRANCH_EMAIL") {
        message = "A branch with this email address already exists. Please use a different email.";
      }
      showActionError(message, { code: apiErr.code });
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !submitting && legalEntities.length > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 backdrop-blur-sm">
      <div
        className="flex min-h-full items-center justify-center px-4 py-16"
        onClick={onOverlayClick}
      >
        <div className="relative w-full max-w-3xl max-h-[calc(100vh-8rem)] flex flex-col bg-white rounded-2xl shadow-2xl border border-bridge-border/40">

          {/* Header */}
          <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-bridge-border/50 rounded-t-2xl bg-gradient-to-r from-bridge-panel to-slate-50/60">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#009BF5]/10 text-[#009BF5]">
                <GitBranch size={18} />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-bold text-bridge-fg leading-tight">Create Branch</h2>
                <p className="text-[11px] text-bridge-muted mt-0.5 leading-tight">
                  Invite people via "Add Team Member" after creating.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="ml-3 shrink-0 flex h-7 w-7 items-center justify-center rounded-lg text-bridge-muted hover:text-bridge-fg hover:bg-bridge-surface transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <form
            id="create-branch-form"
            onSubmit={handleSubmit}
            noValidate
            className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-4 [scrollbar-gutter:stable]"
          >
            <div>
              <MenuSelect
                label="Legal Entity *"
                value={form.legalEntityId}
                onChange={(v) => setField("legalEntityId", v)}
                placeholder={
                  legalEntities.length === 0 ? "No legal entities available" : "Choose legal entity"
                }
                disabled={legalEntities.length === 0}
                options={entityOptions}
              />
              {errors.legalEntityId && (
                <p className="mt-1.5 text-xs text-red-500 font-medium">{errors.legalEntityId}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider mb-1.5">
                Branch Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="Enter branch name"
                aria-invalid={Boolean(errors.name)}
                className={`w-full py-2.5 px-4 bg-white border rounded-xl text-sm text-bridge-fg placeholder:text-bridge-subtle shadow-sm outline-none transition-all ${
                  errors.name
                    ? "border-red-400 focus:ring-2 focus:ring-red-400/20 focus:border-red-400"
                    : "border-bridge-border/80 focus:ring-2 focus:ring-[#009BF5]/20 focus:border-[#009BF5]"
                }`}
              />
              {errors.name && (
                <p className="mt-1.5 text-xs text-red-500 font-medium">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider mb-1.5">
                Branch Email
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="Enter branch email (optional)"
                aria-invalid={Boolean(errors.email)}
                className={`w-full py-2.5 px-4 bg-white border rounded-xl text-sm text-bridge-fg placeholder:text-bridge-subtle shadow-sm outline-none transition-all ${
                  errors.email
                    ? "border-red-400 focus:ring-2 focus:ring-red-400/20 focus:border-red-400"
                    : "border-bridge-border/80 focus:ring-2 focus:ring-[#009BF5]/20 focus:border-[#009BF5]"
                }`}
              />
              {errors.email && (
                <p className="mt-1.5 text-xs text-red-500 font-medium">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider mb-1.5">
                Phone Number
              </label>
              <div className="flex gap-2">
                <div className="w-1/3">
                  <MenuSelect
                    options={phoneCodeOptions}
                    value={phoneCodeValue}
                    maxVisibleOptions={3}
                    onChange={(v) => {
                      const [nextDialCode, nextCountry] = v.split("|");
                      setForm((f) => ({
                        ...f,
                        phoneDialCode: nextDialCode,
                        phoneCountryCode: nextCountry || f.phoneCountryCode,
                      }));
                    }}
                    placeholder="Code"
                    loading={loadingCountries}
                    searchable
                  />
                </div>
                <div className="flex-1">
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={form.phoneNumber}
                    onChange={(e) => setField("phoneNumber", sanitizeNationalPhoneInput(e.target.value))}
                    placeholder="7123456789"
                    className="w-full py-2.5 px-4 bg-white border border-bridge-border/80 rounded-xl text-sm text-bridge-fg placeholder:text-bridge-subtle shadow-sm outline-none transition-all focus:ring-2 focus:ring-[#009BF5]/20 focus:border-[#009BF5]"
                  />
                </div>
              </div>
            </div>

            <LogoUploadField
              label="Branch Logo"
              logoUrl={form.logoUrl}
              previewName={form.name}
              onChange={(logoUrl) => setField("logoUrl", logoUrl ?? "")}
              disabled={submitting}
            />

            <EntityAddressFields
              values={pickAddressValues(form)}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
              disabled={submitting}
            />
          </form>

          {/* Footer */}
          <div className="shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-bridge-border/50 rounded-b-2xl bg-slate-50/60">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-bridge-muted hover:text-bridge-fg border border-bridge-border/70 rounded-xl hover:bg-white transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="create-branch-form"
              disabled={!canSubmit}
              className="px-5 py-2 text-sm font-bold text-white bg-[#009BF5] hover:bg-[#0085d4] rounded-xl shadow-sm disabled:opacity-50 transition-colors"
            >
              {submitting ? "Creating…" : "Create Branch"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function flattenEntities(
  entities: LegalEntityHierarchy[],
  depth = 0,
): (LegalEntityHierarchy & { depth: number })[] {
  return entities.reduce(
    (acc, le) => {
      acc.push({ ...le, depth });
      if (le.subEntities && le.subEntities.length > 0) {
        acc.push(...flattenEntities(le.subEntities, depth + 1));
      }
      return acc;
    },
    [] as (LegalEntityHierarchy & { depth: number })[],
  );
}
